import { Spinner } from './feedback';
import { ClientPackProblems } from './ClientPackProblems';
import { clientPackProgress, type ClientPackProblem } from '@mcpanel/core';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useState } from 'react';
import type { ClientPackVerification as Verification } from '@mcpanel/core';
import { clientModsApi } from '../api/client-mod-templates';
import { errorMessage, useApp } from '../app-context';
import { usePoll } from '../hooks/usePoll';
import { ErrorAlert } from './ui';

const states = { queued: '待ち', running: '実行中', succeeded: '成功', failed: '失敗' };

/** 起動テストの進み具合。ジョブの進み具合と同じメーターで出す */
function VerificationProgress({ check }: { check: Verification }) {
  const progress = clientPackProgress(check);
  if (!progress) return null;
  return (
    <div className="meter" role="group" aria-label="クライアント起動テストの進み具合">
      <div className="meter-head">
        <span>
          {progress.percent}% ・ {progress.label}
        </span>
      </div>
      <div
        className="meter-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress.percent}
        aria-label={`${progress.percent}%`}
      >
        <div className="meter-fill" style={{ width: `${progress.percent}%` }} />
      </div>
    </div>
  );
}
export function ClientPackVerificationView({
  check,
  retry,
  summary = false,
  repair,
  actions,
}: {
  check: Verification;
  retry?: () => Promise<unknown>;
  summary?: boolean;
  repair?: { href: string; label: string };
  actions?: (problem: ClientPackProblem) => ReactNode;
}) {
  const { notify } = useApp();
  const [busy, setBusy] = useState(false);
  const retest = async () => {
    setBusy(true);
    try {
      await retry?.();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack">
      <div className="row">
        <strong>
          {['queued', 'running'].includes(check.state) && <Spinner />}クライアント起動テスト: {states[check.state]}
        </strong>
        {retry && (
          <button
            className="btn small"
            disabled={busy || ['queued', 'running'].includes(check.state)}
            onClick={() => void retest()}
          >
            {busy && <Spinner />}
            再テスト
          </button>
        )}
        <span className="small muted">
          最後に試した日時: {new Date(check.finishedAt ?? check.createdAt).toLocaleString()}
        </span>
      </div>
      <VerificationProgress check={check} />
      {summary && repair && (check.state === 'failed' || check.unavailable.length > 0) && (
        <Link to={repair.href}>{repair.label}</Link>
      )}
      {!summary && (
        <>
          <ClientPackProblems check={check} actions={actions} />
          {!!check.added.length && <p>依存として追加: {check.added.join('、')}</p>}
          {!!check.unavailable.length && (
            <ul>
              {check.unavailable.map((mod, index) => (
                <li key={index}>
                  {mod.title}: {mod.reason ?? '対応版がありません'}
                </li>
              ))}
            </ul>
          )}
          {check.state !== 'succeeded' && (
            <p className="small muted">
              成功するまでは、前に成功した構成を配布します。初回はバニラ互換サーバーは通常の Minecraft、Mod
              系サーバーは準備中になります。
            </p>
          )}
        </>
      )}
    </div>
  );
}
export function ClientPackVerificationCard({
  serverId,
  repair,
}: {
  serverId: string;
  repair?: { href: string; label: string };
}) {
  const { canOperate } = useApp();
  const data = usePoll(() => clientModsApi.verification(serverId), 5000, [serverId]);
  return (
    <>
      <ErrorAlert error={data.error} />
      {data.data && (
        <ClientPackVerificationView
          check={data.data}
          summary
          repair={repair}
          retry={
            canOperate
              ? async () => {
                  await clientModsApi.retest(serverId);
                  await data.reload();
                }
              : undefined
          }
        />
      )}
    </>
  );
}
export function ClientTemplateVerifications({
  templateId,
  selectedServer,
  actions,
  compact = false,
}: {
  templateId: string;
  selectedServer?: string;
  compact?: boolean;
  actions?: (problem: ClientPackProblem) => ReactNode;
}) {
  const { canOperate } = useApp();
  const data = usePoll(() => clientModsApi.verifications(templateId), 5000, [templateId]);
  return (
    <>
      <ErrorAlert error={data.error} />
      {!compact && data.data?.length === 0 && (
        <p className="small muted">サーバーでこのテンプレートを選ぶと、その Minecraft の版で起動テストを行います。</p>
      )}
      {!compact && data.data?.some((entry) => ['queued', 'running'].includes(entry.verification.state)) && (
        <p role="status">
          <Spinner />
          再テストしています
        </p>
      )}
      {compact && (
        <p>
          成功 {data.data?.filter((entry) => entry.verification.state === 'succeeded').length ?? 0} 件・失敗{' '}
          {data.data?.filter((entry) => entry.verification.state === 'failed').length ?? 0} 件
        </p>
      )}
      {!compact &&
        data.data
          ?.toSorted((a, b) => Number(b.verification.state === 'failed') - Number(a.verification.state === 'failed'))
          .map((entry) => (
            <details key={entry.serverId} open={selectedServer === entry.serverId}>
              <summary>
                {entry.serverName} {entry.mcVersion} — {states[entry.verification.state]}
                {entry.verification.state === 'running' && ` ${clientPackProgress(entry.verification)?.percent ?? 0}%`}
              </summary>
              <ClientPackVerificationView
                check={entry.verification}
                actions={actions}
                retry={
                  canOperate
                    ? async () => {
                        await clientModsApi.retest(entry.serverId);
                        await data.reload();
                      }
                    : undefined
                }
              />
            </details>
          ))}
    </>
  );
}
